import Logger from '../lib/logger';
import ImageLoader from './ImageLoader';
import ThemePlayButton from './ThemePlayButton';
import ThemeLoadingAnim from './ThemeLoadingAnim';

export default class ThemeScrubber extends HTMLElement {
  constructor() {
    super();
		this.NAME = `ThemeScrubber`;
		this.INDEX_PLACEHOLDER = `$NUM`;
		this.loader = new ImageLoader();
		this.observer;
		this.dpr = window.devicePixelRatio;
		this.rect;
		this.isPlaying = false;
		this.autoplay = false;
		this.autoplayInterval;
		this.fps = 30;
		this.intervalTime = 1000/this.fps;
		this.currentFrame = 0;
		this.progress = 0;
		this.hasUserInteracted = false;
		this.frames;
		this.canvas = this.querySelector('canvas');
		this.ctx = this.canvas.getContext('2d', { alpha: true }); // change to false if there are performance issues
		this.placeholderDom = this.querySelector('[data-scrubber-placeholder-image]');
		this.sliderDom = this.querySelector('input[type="range"]');
		this.playBtn = this.querySelector('[data-theme-scrubber-play-button]') || this.querySelector('.theme-scrubber__controls theme-play-button');
		this.restartPlayBtn = this.querySelector('[data-theme-scrubber-restart-play-button]');
		this.loadingAnim = this.querySelector('theme-loading-anim');
		this.hasRestartOverlay = Boolean(this.closest('.theme-scrubber-content-module'));
		Logger.info(`${this.NAME} > constructor`);
  }

  connectedCallback() {
		Logger.info(`${this.NAME} > connectedCallback`);
		this.abortController = new AbortController();
		this.init();
  }

  disconnectedCallback() {
		Logger.info(`${this.NAME} > disconnectedCallback`);
		this.abortController.abort();
		this.abortController = null;
		if (this.observer) this.observer.unobserve(this);
  }

  static get observedAttributes() {
    return ['fps', 'autoplay'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
		Logger.info(`${this.NAME} > attributeChangedCallback, ${name} ${newValue}`);
    if (name === 'fps') {
			const fps = parseInt(newValue);
			if (!isNaN(fps)) {
				this.fps = fps;
				this.intervalTime = 1000/this.fps;
			}
		}
		//
		if (name === 'autoplay') {
			this.autoplay = newValue === 'true';
		}
  }

	async init() {
		Logger.info(`${this.NAME} > init`);
		await this.loadImages();
		this.loadingAnim.dataset.loading = false;
		//
		window.addEventListener('resize', () => {
			this.scaleCanvas();
			this.update(this.currentFrame);
		}, { signal: this.abortController.signal });
		//
		if (this.autoplay) this.isPlaying = true;
		this.scaleCanvas();
		this.update(this.currentFrame);
		if (this.placeholderDom) this.placeholderDom.dataset.show = 'false';
		this.initControls();
		this.syncPlayButtons();
		//
		this.observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				//
				if (entry.isIntersecting && this.isPlaying && this.autoplay) {
					this.play();
				}
				//
				if (!entry.isIntersecting && this.isPlaying) {
					this.pause(!this.autoplay);
				}
			})
		})

		this.observer.observe(this);
	}

	async loadImages() {
		const imageSetDoms = this.querySelectorAll('[data-scrubber-image-set]');
		const imageSources = [];
		//
		imageSetDoms.forEach((el) => {
			const url = el.dataset.url;
			const startingFrame = parseInt(el.dataset.startingFrame);
			const endingFrame = parseInt(el.dataset.endingFrame);
			//
			for (let i = startingFrame; i <= endingFrame; i++) {
				imageSources.push(url.replace(this.INDEX_PLACEHOLDER, i));
			}
		});
		//
		this.sliderDom.max = imageSources.length - 1;
		this.frames = await this.loader.loadArray(imageSources);
		return;
	}

	scaleCanvas() {
		this.rect = this.getBoundingClientRect();
		// Set the "actual" size of the canvas
		this.canvas.width = this.rect.width * this.dpr;
		this.canvas.height = this.rect.height * this.dpr;
		// Scale the context to ensure correct drawing operations
		this.ctx.scale(this.dpr, this.dpr);
		// Set the "drawn" size of the canvas
		this.canvas.style.width = `${this.rect.width}px`;
		this.canvas.style.height = `${this.rect.height}px`;
	}

	update(idx) {
		if (!this.frames[idx]) return;
		this.ctx.drawImage(this.frames[idx], 0, 0, this.rect.width, this.rect.height);
		this.progress = (idx / this.sliderDom.max) * 100;
		this.progress += this.progress < 50 ? 0.3 : -0.3;
		this.sliderDom.style.setProperty('--progress', `${this.progress}%`);
		this.currentFrame = idx;
		this.syncPlayButtons();
	}

	play() {
		Logger.info(`${this.NAME} > play`);
		let nextFrame;
		this.isPlaying = true;
		this.syncPlayButtons();
		/* this.playBtn.setAttribute('title', 'pause'); */
		this.autoplayInterval = setInterval(() => {
			nextFrame = this.currentFrame + 1;
			if (nextFrame >= this.frames.length) {
				nextFrame = 0;
			}

			requestAnimationFrame(() => {
				this.update(nextFrame);
				this.sliderDom.value = nextFrame;
			});
		}, this.intervalTime);
	}

	pause(setIsPlaying = true) {
		Logger.info(`${this.NAME} > pause`);
		if (setIsPlaying) {
			this.isPlaying = false;
		}
		if (this.autoplayInterval) {
			clearInterval(this.autoplayInterval);
			this.autoplayInterval = null;
		}
		this.syncPlayButtons();
		/* this.playBtn.setAttribute('title', 'play'); */
	}

	syncPlayButtons() {
		if (this.playBtn) {
			this.playBtn.dataset.playing = this.isPlaying ? 'true' : 'false';
		}
		if (this.restartPlayBtn) {
			const shouldShowRestart = this.hasRestartOverlay && this.hasUserInteracted && !this.isPlaying && this.currentFrame === 0;
			this.restartPlayBtn.dataset.playing = this.isPlaying ? 'true' : 'false';
			this.restartPlayBtn.dataset.show = shouldShowRestart ? 'true' : 'false';
			this.restartPlayBtn.hidden = !shouldShowRestart;
		}
	}

	initControls() {
		Logger.info(`${this.NAME} > initControls`);
		let frameIdx;
		const { signal } = this.abortController.signal;

		// prevent page move when dragging slider on mobile
		this.sliderDom.addEventListener('touchmove', (e) => {
			return false;
		}, { signal, passive: false });

		// update canvas when input changes
		this.sliderDom.addEventListener('input', () => {
			frameIdx = parseInt(this.sliderDom.value);
			this.hasUserInteracted = true;
			if (this.isPlaying) this.pause();
			this.update(frameIdx);
		}, { signal });

		this.sliderDom.addEventListener('pointerup', () => {
			this.syncPlayButtons();
		}, { signal });
		
		if (this.playBtn) {
			this.playBtn.addEventListener('click', () => {
				this.hasUserInteracted = true;
				if (this.isPlaying)  {
					this.pause();
				} else {
					this.play();
				}
			}, { signal });
		}

		if (this.restartPlayBtn) {
			this.restartPlayBtn.addEventListener('click', () => {
				this.hasUserInteracted = true;
				this.play();
			}, { signal });
		}
	}

}

customElements.define('theme-scrubber', ThemeScrubber);
